import { type RuleCondition, severityName, type ThresholdOperator } from '@vision360/contracts';
import { useTranslation } from 'react-i18next';
import { formatDuration, tagLabel } from '@/widgets/_sdk/format';
import { useSources } from '@/widgets/_sdk/hooks';

/** Comparison symbols: language-neutral, the select spells them out in French. */
export const OPERATOR_SYMBOL: Record<ThresholdOperator, string> = {
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
};

const number = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 6 });

/**
 * One French line per condition, for the rules table:
 * « cpu_percent > 90 pendant 5 min sur Serveur maison ».
 */
export function useConditionSummary(): (condition: RuleCondition) => string {
  const { t } = useTranslation(['rules', 'widgets']);
  const sources = useSources();
  const sourceName = (id: string) =>
    sources.data?.sources.find((s) => s.id === id)?.name ?? t('rules:summary.unknownSource');

  return (c) => {
    switch (c.type) {
      case 'metric_threshold': {
        const vars = {
          metric: c.metricName,
          series:
            c.tags && Object.keys(c.tags).length > 0
              ? t('rules:summary.series', { series: tagLabel(c.tags) })
              : '',
          operator: OPERATOR_SYMBOL[c.operator],
          value: number.format(c.value),
          duration: formatDuration(c.forSeconds),
          source: sourceName(c.sourceId),
        };
        return c.forSeconds > 0
          ? t('rules:summary.metricFor', vars)
          : t('rules:summary.metric', vars);
      }
      case 'event_match': {
        const parts = [
          c.kinds?.length
            ? t('rules:summary.eventKinds', { kinds: c.kinds.join(', ') })
            : t('rules:summary.eventAny'),
          c.minSeverity !== undefined &&
            t('rules:summary.eventSeverity', {
              severity: t(`widgets:severity.${severityName(c.minSeverity)}`),
            }),
          c.titleContains && t('rules:summary.eventTitle', { text: c.titleContains }),
        ].filter(Boolean);
        const line = parts.join(', ');
        return c.sourceId
          ? `${line} ${t('rules:summary.onSource', { source: sourceName(c.sourceId) })}`
          : line;
      }
      case 'source_stale':
        return t('rules:summary.stale', {
          source: sourceName(c.sourceId),
          duration: formatDuration(c.staleAfterSeconds),
        });
      case 'absence':
        return t('rules:summary.absence', {
          kind: c.kind,
          duration: formatDuration(c.withinSeconds),
          source: sourceName(c.sourceId),
        });
    }
  };
}
